import { apiClient as api } from "./httpClient";

export interface ProfessionalServiceValueItem {
  id: string;
  professionalRegistration?: string;
  professionalTeam?: string;
  professionalTeamName?: string;
  serviceType?: string;
  serviceDescription?: string;
  serviceValue?: number;
  professionalPaymentVal?: number;
  sessionTimeMinutes?: number;
  sessionTimeHour?: number;
  professionalPaymentInc?: boolean;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  hasActiveDuplicate?: boolean; // inativo cujo serviço já tem outro registro ativo na equipe
  inUse: boolean; // referenciado em agendamento -> não pode editar; excluir vira inativar
}

export interface PagedResult<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface ProfessionalServiceValueListParams {
  professionalRegistration: string;
  professionalTeam?: string;
  active?: boolean; // undefined = todos; true = ativos; false = inativos
  pageNumber: number;
  pageSize: number;
}

export interface ProfessionalServiceValuePayload {
  professionalTeam?: string;
  serviceType?: string;
  serviceValue?: number;
  professionalPaymentVal?: number;
  sessionTimeMinutes?: number;
  professionalPaymentInc?: boolean;
}

export interface ProfessionalTeamOption {
  id: string;
  name: string;
}

// Linha da tela Valores por serviço: profissional elegível da equipe com o
// vínculo ATIVO do serviço, quando houver (valueId nulo = sem serviço).
export interface ServiceProfessionalRow {
  professionalRegistration: string;
  professionalName?: string;
  valueId?: string;
  serviceValue?: number;
  professionalPaymentVal?: number;
  professionalPaymentInc?: boolean;
  sessionTimeMinutes?: number;
  updatedAt?: string;
}

export const professionalServiceValueService = {
  // Somente equipes em que o profissional está inserido (regra 11/07/2026)
  getTeams: async (
    professionalRegistration: string
  ): Promise<ProfessionalTeamOption[]> => {
    const response = await api.get(
      `/professional-service-values/teams/${professionalRegistration}`
    );
    return Array.isArray(response.data) ? response.data : [];
  },
  getByService: async (
    serviceType: string,
    professionalTeam: string
  ): Promise<ServiceProfessionalRow[]> => {
    const response = await api.get("/professional-service-values/by-service", {
      params: { serviceType, professionalTeam },
    });
    return Array.isArray(response.data) ? response.data : [];
  },
  getByProfessional: async (
    params: ProfessionalServiceValueListParams
  ): Promise<PagedResult<ProfessionalServiceValueItem>> => {
    const response = await api.get("/professional-service-values", {
      params: {
        ProfessionalRegistration: params.professionalRegistration,
        ProfessionalTeam: params.professionalTeam || undefined,
        Active: params.active,
        PageNumber: params.pageNumber,
        PageSize: params.pageSize,
      },
    });
    return response.data;
  },
  create: async (
    professionalRegistration: string,
    payload: ProfessionalServiceValuePayload
  ): Promise<void> => {
    await api.post("/professional-service-values", {
      professionalRegistration,
      ...payload,
    });
  },
  update: async (
    id: string,
    payload: ProfessionalServiceValuePayload
  ): Promise<void> => {
    await api.put(`/professional-service-values/${id}`, payload);
  },
  setStatus: async (id: string, active: boolean): Promise<void> => {
    await api.patch(`/professional-service-values/${id}/status`, { active });
  },
  remove: async (id: string): Promise<{ hardDeleted: boolean }> => {
    const response = await api.delete(`/professional-service-values/${id}`);
    return response.data;
  },
  /** Matriz serviço × profissional; sem `serviceType` traz todos os serviços da especialidade. */
  getMatrix: async (params: ServiceValuesMatrixParams): Promise<ServiceValuesMatrix> => {
    const response = await api.get("/professional-service-values/matrix", {
      params: {
        professionalTeam: params.professionalTeam,
        professionalType: params.professionalType,
        ...(params.serviceType ? { serviceType: params.serviceType } : {}),
      },
    });
    return response.data;
  },
  /** Sobe a planilha exportada, já editada. dryRun = prévia. */
  importMatrix: async (file: File, professionalTeam: string, dryRun: boolean) => {
    const body = new FormData();
    body.append("file", file);
    body.append("professionalTeam", professionalTeam);
    body.append("dryRun", String(dryRun));
    const response = await api.post("/professional-service-values/matrix/import", body, {
      headers: { "Content-Type": null },
    });
    return response.data;
  },
  exportMatrixXlsx: async (params: ServiceValuesMatrixParams): Promise<Blob> => {
    const response = await api.get("/professional-service-values/matrix/xlsx", {
      params: {
        professionalTeam: params.professionalTeam,
        professionalType: params.professionalType,
        ...(params.serviceType ? { serviceType: params.serviceType } : {}),
      },
      responseType: "blob",
    });
    return response.data;
  },
};

export interface ServiceValuesMatrixParams {
  professionalTeam: string;
  professionalType: string;
  /** Vazio = todos os serviços ativos da especialidade. */
  serviceType?: string;
}

export interface ServiceValuesMatrixRow {
  professionalRegistration: string;
  professionalName?: string | null;
  /** Nulo = profissional elegível ainda sem valor: a tela oferece incluir. */
  valueId?: string | null;
  serviceValue?: number | null;
  professionalPaymentVal?: number | null;
  professionalPaymentInc?: boolean | null;
  sessionTimeMinutes?: number | null;
  updatedAt?: string | null;
}

export interface ServiceValuesMatrixService {
  serviceTypeId: string;
  description: string;
  origin: string;
  healthPlanName?: string | null;
  procedureCode?: string | null;
  professionals: ServiceValuesMatrixRow[];
}

export interface ServiceValuesMatrix {
  teamName: string;
  professionalTypeName: string;
  services: ServiceValuesMatrixService[];
}
